"use client";

import { IconAlertWarningOutline18 } from "nucleo-ui-outline-18";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { isJsonSchemaBuilderCompatible } from "@/lib/workflow/inputSchema";
import { useEffect, useMemo, useRef, useState } from "react";
import InputSchemaBuilder, { fieldsToJsonSchema, jsonSchemaToFields, type SchemaField } from "./InputSchemaBuilder";
import JsonEditorBase from "./JsonEditorBase";
interface JsonSchemaBuilderInputProps {
  label: string;
  description?: string;
  value: string | object;
  onChange: (value: Record<string, unknown> | undefined) => void;
  placeholder?: string;
}
const valueToString = (value: string | object): string => {
  if (value === null || value === undefined) return "{}";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return "{}";
  }
};
function parseJsonSchema(value: string): Record<string, unknown> | undefined {
  if (!value.trim()) return undefined;
  const parsed = JSON.parse(value);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("Schema must be a JSON object");
  }
  return parsed as Record<string, unknown>;
}
export default function JsonSchemaBuilderInput({
  label,
  description,
  value,
  onChange,
  placeholder = "{}"
}: JsonSchemaBuilderInputProps) {
  const displayValue = useMemo(() => valueToString(value), [value]);
  const [jsonValue, setJsonValue] = useState(displayValue);
  const [builderFields, setBuilderFields] = useState<SchemaField[]>(() => {
    try {
      return jsonSchemaToFields(parseJsonSchema(displayValue));
    } catch {
      return [];
    }
  });
  const previousDisplayValueRef = useRef(displayValue);
  useEffect(() => {
    if (previousDisplayValueRef.current === displayValue) return;
    previousDisplayValueRef.current = displayValue;
    setJsonValue(displayValue);
    try {
      setBuilderFields(jsonSchemaToFields(parseJsonSchema(displayValue)));
    } catch {
      setBuilderFields([]);
    }
  }, [displayValue]);
  const parsedSchema = useMemo(() => {
    try {
      return parseJsonSchema(jsonValue);
    } catch {
      return undefined;
    }
  }, [jsonValue]);
  const canUseFormMode = isJsonSchemaBuilderCompatible(parsedSchema);
  const [mode, setMode] = useState<"form" | "json">(() => canUseFormMode ? "form" : "json");
  useEffect(() => {
    if (!canUseFormMode && mode === "form") {
      setMode("json");
    }
  }, [canUseFormMode, mode]);
  const handleJsonChange = (nextValue: string) => {
    setJsonValue(nextValue);
    try {
      const schema = parseJsonSchema(nextValue);
      setBuilderFields(jsonSchemaToFields(schema));
      onChange(schema && Object.keys(schema).length > 0 ? schema : undefined);
    } catch {
      // Keep local draft while JSON is invalid; only commit valid schemas.
    }
  };
  const handleFieldsChange = (fields: SchemaField[]) => {
    setBuilderFields(fields);
    const schema_0 = fieldsToJsonSchema(fields);
    const nextJsonValue = schema_0 ? JSON.stringify(schema_0, null, 2) : "{}";
    setJsonValue(nextJsonValue);
    onChange(schema_0);
  };
  return <Tabs value={mode} onValueChange={value_0 => setMode(value_0 as "form" | "json")} size="sm">
      <div className="space-y-3">
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-3">
            <label className="field-label block">{label}</label>
            <TabsList>
              <TabsTrigger value="form" disabled={!canUseFormMode}>
                UI form
              </TabsTrigger>
              <TabsTrigger value="json">JSON</TabsTrigger>
            </TabsList>
          </div>
          {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}
          {!canUseFormMode && <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-900/60 dark:bg-amber-950/30">
              <IconAlertWarningOutline18 className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
              <p className="text-xs text-amber-800 dark:text-amber-300">
                This schema uses advanced JSON features. Edit it in JSON mode.
              </p>
            </div>}
        </div>
        <TabsContent value="form" className="mt-0">
          <InputSchemaBuilder initialFields={builderFields} onChange={handleFieldsChange} />
        </TabsContent>
        <TabsContent value="json" className="mt-0">
          <JsonEditorBase label="" value={jsonValue} onChange={handleJsonChange} placeholder={placeholder} minHeight="180px" customValidate={jsonString => {
          try {
            parseJsonSchema(jsonString || "{}");
            return {
              valid: true
            };
          } catch (error) {
            return {
              valid: false,
              error: error instanceof Error ? error.message : "Invalid JSON schema"
            };
          }
        }} />
        </TabsContent>
      </div>
    </Tabs>;
}